// E-commerce (sito WooCommerce): chiamate all'API, formattazioni e componenti condivisi
// dalle pagine ordini, clienti e fatture. L'API inoltra le rotte /woo/* a NIL, che
// risponde {success, message, data}: qui restituiamo direttamente data.
// Parametri e campi sono quelli documentati in nil/docs/woocommerce.md.
import {useNavigate} from 'react-router-dom';
import {apiDownload, apiGet, apiPost, handleApiError} from './api';

// ---------- chiamate ----------

async function wooGet(path, params) {
    return (await apiGet(path, params)).data;
}

// {totale, ordini, avvisi}; filtri: from, to (YYYY-MM-DD), status (lista con virgole), customer_id
export const getOrdini = (filtri) => wooGet('/woo/ordini', filtri);
// {ordine, avvisi}
export const getOrdine = (id) => wooGet(`/woo/ordini/${id}`);
// {totale, clienti, avvisi}; filtri: search, email, role
export const getClienti = (filtri) => wooGet('/woo/clienti', filtri);
// {cliente, avvisi}
export const getCliente = (id) => wooGet(`/woo/clienti/${id}`);
// {totale, fatture, note_credito, documenti}; filtri: from, to, tipo (fattura|nota_credito), stato
export const getFatture = (filtri) => wooGet('/woo/fatture', filtri);

// Un documento POP e' identificato da rif = {id, tipo, parentId}: id dell'ordine per le
// fatture, id del rimborso (con parentId = ordine) per le note di credito.
const rifParams = ({tipo, parentId}) => ({tipo, parent_id: parentId || undefined});

export function rifDaDocumento(d) {
    return {id: d.document_id, tipo: d.document_type === 'credit_note' ? 'nota_credito' : 'fattura', parentId: d.parent_id};
}

// Dati del documento (numero, flag inviata, ...). 404 se l'ordine non ha una fattura.
export const getFattura = (rif) => wooGet(`/woo/fatture/${rif.id}`, rifParams(rif));
export const scaricaFattura = (rif, formato) => apiDownload(`/woo/fatture/${rif.id}/${formato}`, rifParams(rif));
export const scaricaZipFatture = (filtri) => apiDownload('/woo/fatture/zip', filtri);
// Imposta o toglie il flag "inviata" del plugin POP; restituisce il documento aggiornato.
export const segnaInviata = async (rif, inviata) =>
    (await apiPost(`/woo/fatture/${rif.id}/inviata`, {inviata}, rifParams(rif))).data;

// ---------- scritture sugli ordini (NIL: docs/woocommerce.md, "Scritture sugli ordini") ----------
// ATTENZIONE: alcune fanno partire una MAIL AL CLIENTE dal sito. La UI lo dice sempre prima
// di confermare; NIL lo ripete in message e in data.mail_cliente.

// Nome della mail che WooCommerce manda al cliente passando da `da` a `a`, oppure null se
// non parte nessuna mail (tabella in nil docs/woocommerce.md, "Scritture sugli ordini").
export function mailPerCambioStato(da, a) {
    switch (a) {
        case 'completed':
            return 'Ordine completato';
        case 'refunded':
            return 'Ordine rimborsato';
        case 'processing':
            return ['pending', 'failed', 'on-hold', 'cancelled'].includes(da) ? 'Ordine in lavorazione' : null;
        case 'on-hold':
            return ['pending', 'failed', 'cancelled'].includes(da) ? 'Ordine in sospeso' : null;
        default:
            return null;
    }
}

// {ordine, stato_precedente, stato, invariato, mail_cliente, mail, nota, messaggio}
export const cambiaStatoOrdine = async (id, stato, nota) =>
    unwrap(await apiPost(`/woo/ordini/${id}/stato`, {stato, nota: nota || undefined}));
// {totale, note: [{id, author, date_created, nota, al_cliente}]}
export const getNoteOrdine = (id) => wooGet(`/woo/ordini/${id}/note`);
// {nota, mail_cliente, messaggio}; inviaAlCliente=true -> il sito manda la nota al cliente via mail
export const aggiungiNotaOrdine = async (id, nota, inviaAlCliente) =>
    unwrap(await apiPost(`/woo/ordini/${id}/note`, {nota, invia_al_cliente: inviaAlCliente}));
// {spedizione: {corriere, numero, url, data_spedizione}}; 404 se l'ordine non ha tracking
export const getSpedizioneOrdine = (id) => wooGet(`/woo/ordini/${id}/spedizione`);
// dati: {corriere, numero, url, data, invia_al_cliente}; sovrascrive; non cambia lo stato
export const salvaSpedizioneOrdine = async (id, dati) =>
    unwrap(await apiPost(`/woo/ordini/${id}/spedizione`, dati));

// data + message di NIL, che per le scritture dice cosa e' successo (mail comprese).
function unwrap(res) {
    return {...res.data, messaggio: res.message};
}

// Errore di una scrittura: sessione scaduta -> login; il resto diventa un testo per l'utente.
// Nessun retry: una nota mandata due volte al cliente e' un danno. Se NIL dice che lo stato
// (o il tracking) e' stato salvato ma la nota no, lo spieghiamo invece di far ripetere.
export function testoErroreScrittura(err) {
    if (err.status === 401) {
        handleApiError(err);
        return null;
    }
    console.error(err);
    let testo = err.message;
    if (/nota NON/i.test(testo)) {
        testo += ' La modifica e\' stata salvata: non ripetere l\'operazione, aggiungi la nota dal riquadro Note.';
    }
    return testo;
}

// Errori nelle pagine e-commerce: sessione scaduta -> login, il resto viene mostrato nella
// pagina (NIL non configurato, sito non raggiungibile, ...) invece che con un alert.
export function gestisciErrore(setErrore) {
    return (err) => {
        if (err.status === 401) return handleApiError(err);
        console.error(err);
        setErrore(err.message);
    };
}

// ---------- formattazioni ----------

// Stati ordine WooCommerce: etichetta e colore Bootstrap del badge.
const STATI_ORDINE = {
    'pending': ['In attesa di pagamento', 'warning'],
    'processing': ['In lavorazione', 'primary'],
    'on-hold': ['In sospeso', 'secondary'],
    'completed': ['Completato', 'success'],
    'cancelled': ['Annullato', 'dark'],
    'refunded': ['Rimborsato', 'info'],
    'failed': ['Fallito', 'danger'],
    'checkout-draft': ['Bozza', 'light'],
};

export const STATI_ORDINE_FILTRO = Object.entries(STATI_ORDINE).map(([valore, [etichetta]]) => ({valore, etichetta}));

export function StatoOrdine({stato}) {
    const [etichetta, colore] = STATI_ORDINE[stato] ?? [stato, 'secondary'];
    return <span className={`badge text-bg-${colore}`}>{etichetta}</span>;
}

export function TipoDocumento({tipo}) {
    return tipo === 'credit_note'
        ? <span className="badge text-bg-warning">Nota di credito</span>
        : <span className="badge text-bg-secondary">Fattura</span>;
}

// Importi WooCommerce: stringhe decimali ("45.90") o numeri.
export function euro(importo, valuta) {
    const n = Number(importo);
    if (importo === undefined || importo === null || importo === '' || Number.isNaN(n)) return '';
    return new Intl.NumberFormat('it-IT', {style: 'currency', currency: valuta || 'EUR'}).format(n);
}

// "2026-05-03T10:15:42" -> "03/05/2026 10:15"; "2026-05-03" -> "03/05/2026"
export function dataOra(iso) {
    if (!iso) return '';
    const [d, t] = String(iso).split('T');
    const [a, m, g] = d.split('-');
    if (!a || !m || !g) return iso;
    return `${g}/${m}/${a}` + (t ? ' ' + t.slice(0, 5) : '');
}

export function soloData(iso) {
    return dataOra(iso).slice(0, 10);
}

// Timestamp del plugin (secondi Unix, stringa ISO o vuoto) -> data leggibile.
export function dataTimestamp(v) {
    if (v === null || v === undefined || v === '' || v === 0) return '';
    if (typeof v === 'number' || /^\d+$/.test(String(v))) {
        const d = new Date(Number(v) * 1000);
        return d.toLocaleString('it-IT', {dateStyle: 'short', timeStyle: 'short'});
    }
    return dataOra(v);
}

// Nome da mostrare per un indirizzo di fatturazione: azienda oppure nome e cognome.
export function nominativo(ind) {
    if (!ind) return '';
    const persona = [ind.first_name, ind.last_name].filter(Boolean).join(' ');
    return ind.company ? (persona ? `${ind.company} (${persona})` : ind.company) : persona;
}

// Date per i filtri (input type=date), nel fuso locale.
export function oggi() {
    return giorniFa(0);
}

export function giorniFa(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return isoLocale(d);
}

export function primoDelMese() {
    const d = new Date();
    d.setDate(1);
    return isoLocale(d);
}

function isoLocale(d) {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// ---------- componenti ----------

export function Errore({messaggio}) {
    if (!messaggio) return null;
    return <div className="alert alert-danger">{messaggio}</div>;
}

export function Avvisi({avvisi}) {
    if (!avvisi?.length) return null;
    return (
        <div className="alert alert-warning small">
            <ul className="mb-0 ps-3">{avvisi.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
    );
}

// Indirizzo di fatturazione o spedizione in una card.
export function Indirizzo({titolo, indirizzo}) {
    const i = indirizzo ?? {};
    const righe = [
        nominativo(i),
        i.address_1, i.address_2,
        [i.postcode, i.city, i.state && `(${i.state})`].filter(Boolean).join(' '),
        i.country,
    ].filter(Boolean);
    return (
        <div className="card h-100">
            <div className="card-body">
                <h6 className="card-title">{titolo}</h6>
                {righe.length === 0 && <span className="text-secondary">Non indicato</span>}
                {righe.map((r, k) => <div key={k}>{r}</div>)}
                {i.email && <div className="mt-2"><i className="bi-envelope me-2"></i><a href={`mailto:${i.email}`}>{i.email}</a></div>}
                {i.phone && <div><i className="bi-telephone me-2"></i>{i.phone}</div>}
            </div>
        </div>
    );
}

// Tabella ordini condivisa tra elenco ordini e scheda cliente; la riga apre l'ordine.
export function TabellaOrdini({ordini}) {
    const navigate = useNavigate();
    return (
        <div className="table-responsive">
            <table className="table table-hover mb-0">
                <thead>
                <tr>
                    <th>Ordine</th>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Stato</th>
                    <th>Pagamento</th>
                    <th className="text-end">Totale</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {ordini.map((o) => (
                    <tr key={o.id} className="riga-cliccabile" onClick={() => navigate(`/ordini/${o.id}`)}>
                        <td className="fw-semibold">#{o.number}</td>
                        <td className="text-nowrap">{dataOra(o.date_created)}</td>
                        <td>
                            {nominativo(o.billing) || <span className="text-secondary">n.d.</span>}
                            {o.customer_id === 0 && <span className="badge text-bg-light ms-2">ospite</span>}
                        </td>
                        <td><StatoOrdine stato={o.status}/></td>
                        <td className="text-secondary">{o.payment_method_title}</td>
                        <td className="text-end text-nowrap">{euro(o.total, o.currency)}</td>
                        <td className="text-end"><i className="bi-chevron-right text-secondary"></i></td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

// Pulsanti XML / PDF di un documento POP.
export function BottoniDownload({rif, piccoli}) {
    const cls = 'btn btn-outline-secondary' + (piccoli ? ' btn-sm' : '');
    const scarica = (formato) => (e) => {
        e.stopPropagation();
        scaricaFattura(rif, formato).catch((err) => err.status === 401 ? handleApiError(err) : alert(err.message));
    };
    return (
        <div className="btn-group">
            <button type="button" className={cls} onClick={scarica('xml')} title="Scarica XML FatturaPA">
                <i className="bi-filetype-xml me-1"></i>XML
            </button>
            <button type="button" className={cls} onClick={scarica('pdf')} title="Scarica PDF">
                <i className="bi-filetype-pdf me-1"></i>PDF
            </button>
        </div>
    );
}

// Flag "inviata" del plugin POP con pulsante per cambiarlo. documento = dati da /woo/fatture/:id
// (null = non ancora caricati); onAggiornato riceve il documento restituito dopo la modifica.
export function FlagInviata({rif, documento, onAggiornato, piccoli}) {
    if (!documento) return <span className="spinner-border spinner-border-sm text-secondary" role="status"></span>;
    if (documento.errore) return <span className="text-danger small" title={documento.errore}>n.d.</span>;

    const inviata = documento.invoice_sent === 'sent';
    const quando = dataTimestamp(documento.invoice_sent_at);

    function cambia(e) {
        e.stopPropagation();
        const nuovo = !inviata;
        const numero = documento.invoice_number ? ` n. ${documento.invoice_number}` : '';
        if (!window.confirm(`Segnare il documento${numero} come ${nuovo ? 'inviato' : 'NON inviato'}?`)) return;
        segnaInviata(rif, nuovo)
            .then(onAggiornato)
            .catch((err) => err.status === 401 ? handleApiError(err) : alert(err.message));
    }

    return (
        <span className="d-inline-flex align-items-center gap-2">
            {inviata
                ? <span className="badge text-bg-success" title={quando && `Inviata il ${quando}`}>Inviata</span>
                : <span className="badge text-bg-secondary">Da inviare</span>}
            <button type="button" className={'btn btn-link p-0' + (piccoli ? ' small' : '')} onClick={cambia}>
                {inviata ? 'segna da inviare' : 'segna inviata'}
            </button>
        </span>
    );
}
